import React, { useMemo, useState, useEffect } from 'react'
import { makeStyles, Typography } from '@material-ui/core'
import moment from 'moment-timezone'
import { type Activity as ActivityInterface, type MessageActivityDetails } from '@paintscout/api'
import { motion } from 'framer-motion'
import classnames from 'classnames'
import Skeleton from '@paintscout/ui/components/Skeleton'
import Grid from '@paintscout/ui/components/Grid'
import { useTranslation } from '@paintscout/ui'
import type { WithUseStyles } from '@paintscout/ui/styles'
import Activity from '../Activity/Activity'
import type { ActivityProps } from '../Activity/Activity'
import { ActivityTimelineContext } from './ActivityTimelineContext'

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: 'hidden'
  },
  border: {
    position: 'relative',
    '&:after': {
      zIndex: 0,
      display: 'block',
      content: "''",
      borderLeftStyle: 'solid',
      borderLeftWidth: 1,
      borderColor: theme.paintscout.palette.brand.Neutral.P90,
      position: 'absolute',
      left: 26,
      top: 44,
      bottom: -7
    }
  },
  heading: {
    ...theme.typography.overline,
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    color: theme.paintscout.palette.brand.Neutral.P70,
    position: 'relative'
  },
  shimRow: {
    display: 'block',
    width: '100%',
    borderRadius: '5px',
    overflow: 'hidden',
    margin: '10px 0'
  },
  shim: {
    display: 'block',
    color: 'initial',
    height: 40,
    width: '100%',
    margin: 'auto 0'
  },

  activity: {
    alignItems: 'center',
    '&:last-of-type': {
      paddingBottom: theme.spacing(2)
    }
  },
  day: {}
}))

export interface ActivityTimelineProps extends WithUseStyles<typeof useStyles> {
  activities: ActivityInterface[]
  ascending?: boolean
  loading?: boolean
  shimRowAmount?: number
  actions?: boolean
  onAction?: (action: string, activity: ActivityInterface) => any
  disabled?: boolean

  /**
   * If true, render with quote sidebar style adjustments
   */
  isQuote?: boolean

  /**
   * Show the additional content for specific activities (events, email content, etc)
   */
  showContent?: boolean

  /**
   * Includes information about the doc type in each activity.
   * Set this to true if you're showing activities from different docs (such as the dashboard)
   *
   * Example descriptions:
   *
   * true - "Quote was Edited by Bob Smith"
   * false - "Edited by Bob Smith"
   */
  showDocTypeDetails?: boolean

  /**
   * Change the component that renders the Activity
   */
  ActivityComponent?: React.ComponentType<ActivityProps>

  /**
   * The document currently being viewed. When set, notes whose `details.pinnedOn` includes this
   * id are lifted into a "Pinned" section at the top of the feed. Only the deal/contact/company
   * notes feed passes this; other feeds leave it undefined. See #21136.
   */
  pinnedDocId?: string
}

const DAY_FORMAT = 'MMM Do, YYYY'
const DAY_KEY_TODAY = 'Today'
const DAY_KEY_YESTERDAY = 'Yesterday'
// Pinned notes are lifted out of their day group into a dedicated section at the very top,
// regardless of age. Pinning is per-viewed-document, so a note counts as pinned only when its
// `details.pinnedOn` includes the doc currently being viewed. See #21136.
const DAY_KEY_PINNED = 'Pinned'

function isPinnedOn(activity: ActivityInterface, pinnedDocId?: string): boolean {
  if (!pinnedDocId) {
    return false
  }
  return Boolean((activity.details as { pinnedOn?: string[] })?.pinnedOn?.includes(pinnedDocId))
}

function ActivityTimeline(props: ActivityTimelineProps) {
  const {
    activities,
    loading,
    shimRowAmount,
    showContent,
    showDocTypeDetails,
    ActivityComponent,
    ascending,
    onAction,
    actions,
    isQuote,
    disabled,
    pinnedDocId
  } = props
  const classes = useStyles(props)
  const { __, lang } = useTranslation()

  // The day-group keys stay canonical (English 'Today'/'Yesterday'/'Pinned' or an en-formatted
  // DAY_FORMAT date) so the grouping/sort logic below is unaffected. Localize only at display:
  // the relative labels via the dictionary, and the date headers by re-formatting under the
  // painter's locale (month name + ordinal). moment's global locale isn't reliably the painter's
  // here, so we force it per-call with .locale(lang).
  const dayHeading = (dayKey: string | number): string => {
    const day = String(dayKey)
    if (day === DAY_KEY_PINNED) return __('Pinned')
    if (day === DAY_KEY_TODAY) return __('Today')
    if (day === DAY_KEY_YESTERDAY) return __('Yesterday')
    const parsed = moment(day, DAY_FORMAT)
    return parsed.isValid() ? parsed.locale(lang).format(DAY_FORMAT) : day
  }

  const filteredActivities = useMemo(() => activities.filter(shouldDisplayActivity), [activities])

  const sorted = useMemo(
    () =>
      // [...] to prevent mutation
      [...filteredActivities].sort((a, b) => {
        const direction = ascending ? -1 : 1

        return a.details?.timestamp > b.details?.timestamp ? -1 * direction : 1 * direction
      }),
    [filteredActivities, ascending]
  )

  const groupedByDay = useMemo(() => {
    return sorted.reduce((byDay: Record<string, ActivityInterface[]>, activity) => {
      const day = moment(activity.details?.timestamp)
      let key = ''

      if (isPinnedOn(activity, pinnedDocId)) {
        key = DAY_KEY_PINNED
      } else if (day.startOf('day').isSame(moment().startOf('day'))) {
        key = DAY_KEY_TODAY
      } else if (day.startOf('day').isSame(moment().startOf('day').subtract(1, 'day'))) {
        key = DAY_KEY_YESTERDAY
      } else {
        key = day.startOf('day').format(DAY_FORMAT)
      }

      return {
        ...byDay,
        [key]: [...(byDay[key] ?? []), activity]
      }
    }, {})
  }, [sorted, ascending, pinnedDocId])

  const dayKeys = useMemo(
    () =>
      Object.keys(groupedByDay).sort((a, b) => {
        const direction = ascending ? -1 : 1

        // Pinned section always floats to the very top, independent of sort direction.
        if (a === DAY_KEY_PINNED) {
          return -1
        }

        if (b === DAY_KEY_PINNED) {
          return 1
        }

        if (a === DAY_KEY_TODAY) {
          return -1 * direction
        }

        if (a === DAY_KEY_YESTERDAY) {
          return b === DAY_KEY_TODAY ? 1 * direction : -1 * direction
        }

        return moment(a, DAY_FORMAT).isAfter(moment(b, DAY_FORMAT)) ? -1 * direction : 1 * direction
      }),
    [groupedByDay, ascending]
  )

  const prevNewestActivity = usePrevNewestActivity(sorted, ascending)

  const value = useMemo(
    () => ({
      activities: filteredActivities,
      pinnedDocId
    }),
    [filteredActivities, pinnedDocId]
  )

  return (
    <ActivityTimelineContext.Provider value={value}>
      <div className={classes.root}>
        {loading ? (
          Array.from({ length: shimRowAmount }).map((_, index) => (
            <motion.div
              key={index}
              className={classes.shimRow}
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: {
                  delay: 0.2
                }
              }}
            >
              <Skeleton height="55px" width="100%" />
            </motion.div>
          ))
        ) : (
          <Grid container spacing={2}>
            {dayKeys.map((day: keyof typeof groupedByDay) => (
              <Grid key={day} item xs={12} className={classes.day}>
                <Typography variant="h6" component="h1" className={classes.heading}>
                  {dayHeading(day)}
                </Typography>
                <Grid container spacing={1}>
                  {groupedByDay[day].map((activity, index) => {
                    const animate =
                      prevNewestActivity && activity?.details?.timestamp > prevNewestActivity?.details?.timestamp

                    return (
                      <Grid
                        key={activity._id}
                        item
                        xs={12}
                        className={classnames(
                          classes.activity,
                          index === groupedByDay[day].length - 1 ? undefined : classes.border
                        )}
                      >
                        <ActivityComponent
                          disabled={disabled}
                          activity={activity}
                          showContent={showContent}
                          showDocTypeDetails={showDocTypeDetails}
                          animate={animate}
                          actions={actions}
                          onAction={onAction}
                          isQuote={isQuote}

                          // there are performance issues when ascending and loading more messages (because of reverse-direction scroll)
                          // layout={ascending ? null : 'position'}
                        />
                      </Grid>
                    )
                  })}
                </Grid>
              </Grid>
            ))}
          </Grid>
        )}
      </div>
    </ActivityTimelineContext.Provider>
  )
}

/**
 * Returns the previous 'newest' activity so we know which activities should be animated
 */
function usePrevNewestActivity(sortedActivities: ActivityInterface[], ascending: boolean) {
  const [prevNewestActivity, setPrevNewestActivity] = useState(
    sortedActivities.length > 0 && sortedActivities[ascending ? sortedActivities.length - 1 : 0]
  )

  useEffect(() => {
    setPrevNewestActivity(sortedActivities.length > 0 && sortedActivities[ascending ? sortedActivities.length - 1 : 0])
  }, [sortedActivities])

  return prevNewestActivity
}

function shouldDisplayActivity(activity: ActivityInterface) {
  if (activity?.details?.activityType !== 'message') {
    return true
  }

  const { contact } = activity.details
  const messageDetails = activity.details as MessageActivityDetails | undefined

  const hasSmsRecipient = !!messageDetails?.message?.smsTo
  const hasContactEmail = !!contact?.email
  const isChatMessage = !!messageDetails?.message?.text

  return hasSmsRecipient || hasContactEmail || isChatMessage
}

ActivityTimeline.defaultProps = {
  activities: [],
  shimRowAmount: 10,
  ActivityComponent: Activity,
  actions: true
}

export default ActivityTimeline
